import {
  BloomEffect,
  EffectComposer,
  EffectPass,
  FXAAEffect,
  OutlineEffect,
  RenderPass,
  ToneMappingEffect,
  ToneMappingMode
} from "postprocessing";
import {
  type Camera,
  HalfFloatType,
  Mesh,
  type Scene,
  type WebGLRenderer
} from "three";

import {
  DEFAULT_BLOOM_STRENGTH,
  DEFAULT_OUTLINE_COLOR,
  DEFAULT_OUTLINE_EDGE_STRENGTH
} from "./renderConstants";
import type { WorldObjectAPI } from "./world/WorldObject";

export class PostProcessing {
  private readonly composer: EffectComposer;
  private readonly outlineEffect: OutlineEffect;
  private readonly bloom = new BloomEffect({
    mipmapBlur: true,
    intensity: DEFAULT_BLOOM_STRENGTH,
    luminanceThreshold: 1,
    luminanceSmoothing: 0.1
  });

  constructor(
    renderer: WebGLRenderer,
    scene: Scene,
    camera: Camera,
    software: boolean
  ) {
    this.outlineEffect = new OutlineEffect(scene, camera, {
      visibleEdgeColor: DEFAULT_OUTLINE_COLOR,
      edgeStrength: DEFAULT_OUTLINE_EDGE_STRENGTH
    });

    this.composer = new EffectComposer(renderer, {
      frameBufferType: HalfFloatType,
      multisampling: software ? 0 : 2
    });
    this.composer.addPass(new RenderPass(scene, camera));
    this.composer.addPass(
      new EffectPass(
        camera,
        this.outlineEffect,
        ...(software ? [] : [this.bloom]),
        new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC })
      )
    );
    if (software)
      this.composer.addPass(new EffectPass(camera, new FXAAEffect()));
  }

  setBloomStrength(strength: number) {
    this.bloom.intensity = strength;
  }

  setSize(width: number, height: number) {
    this.composer.setSize(width, height);
  }

  render(deltaSeconds: number, worldObjects: Iterable<WorldObjectAPI>) {
    this.applyOutlines(worldObjects);
    this.composer.render(deltaSeconds);
  }

  dispose() {
    this.composer.dispose();
  }

  private applyOutlines(worldObjects: Iterable<WorldObjectAPI>) {
    const selection = this.outlineEffect.selection;
    selection.clear();

    let settingsApplied = false;
    for (const worldObject of worldObjects) {
      const outline = worldObject.getOutline?.() ?? null;
      if (outline === null) continue;

      if (!settingsApplied) {
        this.outlineEffect.visibleEdgeColor.setHex(outline.color);
        this.outlineEffect.hiddenEdgeColor.setHex(outline.color);
        this.outlineEffect.edgeStrength = outline.edgeStrength;
        settingsApplied = true;
      }
      worldObject.root.traverse((object) => {
        if (object instanceof Mesh) selection.add(object);
      });
    }
  }
}
